import React, { useMemo, useState, useEffect, useRef } from 'react';
import PoojaTypes from '../components/PoojaTypes';
import HowItWorks from '../components/HowItWorks';
import BookingForm from '../components/BookingForm';
import Testimonials from '../components/Testimonials';
import PageBackground from '../components/PageBackground';
import PoojaFilters from '../components/PoojaFilters';
import { poojaTypesData } from '../config/poojaConfig';
import { useLanguage } from '../contexts/LanguageContext';
import apiService from '../services/api';
import './OnlinePooja.css';

const createEmptySelections = () => ({
  deity: [],
  tithis: [],
  dosha: [],
  benefits: [],
  location: []
});

const normalizeValue = (value = '') => String(value).toLowerCase().trim();

const getCommonValue = (selectedValues = [], poojaValues = []) => {
  const normalizedPoojaValues = (poojaValues || []).map((value) => normalizeValue(value));
  return (selectedValues || []).find((value) => normalizedPoojaValues.includes(normalizeValue(value))) || null;
};

const HIGHEST_DEMAND_PROBLEM_SOLVING_POOJAS = [
  'Mahalakshmi Puja',
  'Kubera Lakshmi Puja',
  'Ganesh Puja',
  'Navgraha Puja',
  'Manglik Dosha Nivaran',
  'Katyayani Puja',
  'Shiva-Parvati Vivah Puja',
  'Mahamrityunjaya Jaap',
  'Hanuman Puja',
  'Rudrabhishek',
  'Shani Saade Sati Puja',
  'Rahu-Ketu Shanti',
  'Kaal Sarp Dosh Puja',
  'Pitru Dosha Nivaran',
  'Sundarkand Path',
  'Bhagavad Gita Path',
  'Guru Puja',
  'Meditation/Yoga rituals'
];

const DEFAULT_CATEGORY_POOJA_IDS = new Set([
  'daily',
  'special',
  'festival',
  'remedial',
  'house-warming',
  'success'
]);

const includesAny = (source, keywords) => keywords.some((keyword) => source.includes(keyword));

const getHighDemandProblemSolvingTitle = ({ benefit, deity, dosha }) => {
  const deityNorm = normalizeValue(deity);
  const doshaNorm = normalizeValue(dosha);
  const benefitNorm = normalizeValue(benefit);
  const merged = `${deityNorm} ${doshaNorm} ${benefitNorm}`;

  // Astrology remedies
  if (includesAny(merged, ['kaal sarp'])) return 'Kaal Sarp Dosh Puja';
  if (includesAny(merged, ['pitru dosha', 'ancestor'])) return 'Pitru Dosha Nivaran';
  if (includesAny(merged, ['rahu', 'ketu'])) return 'Rahu-Ketu Shanti';
  if (includesAny(merged, ['shani', 'sade sati', 'saade sati'])) return 'Shani Saade Sati Puja';
  if (includesAny(merged, ['mangal dosha', 'mangal', 'manglik'])) return 'Manglik Dosha Nivaran';

  // Marriage & relationship
  if (includesAny(merged, ['marriage', 'relationship', 'love'])) {
    if (includesAny(merged, ['katyayani'])) return 'Katyayani Puja';
    if (includesAny(merged, ['shiva', 'parvati'])) return 'Shiva-Parvati Vivah Puja';
  }

  // Health & protection
  if (includesAny(merged, ['health', 'protection'])) {
    if (includesAny(merged, ['shiva', 'mrityunjaya'])) return 'Mahamrityunjaya Jaap';
    if (includesAny(merged, ['hanuman', 'enemy', 'obstacle'])) return 'Hanuman Puja';
    if (includesAny(merged, ['rudra', 'shiva'])) return 'Rudrabhishek';
  }

  // Career & money
  if (includesAny(merged, ['wealth', 'prosperity', 'career', 'money', 'professional'])) {
    if (includesAny(merged, ['kubera'])) return 'Kubera Lakshmi Puja';
    if (includesAny(merged, ['lakshmi', 'mahalakshmi'])) return 'Mahalakshmi Puja';
    if (includesAny(merged, ['ganesha', 'ganesh', 'obstacle'])) return 'Ganesh Puja';
    if (includesAny(merged, ['navgraha', 'navagraha', 'planet'])) return 'Navgraha Puja';
  }

  // Spiritual & emotional peace
  if (includesAny(merged, ['spiritual', 'mental', 'peace', 'negativity'])) {
    if (includesAny(merged, ['hanuman', 'courage', 'enemy'])) return 'Sundarkand Path';
    if (includesAny(merged, ['guru'])) return 'Guru Puja';
    if (includesAny(merged, ['yoga', 'meditation'])) return 'Meditation/Yoga rituals';
    return 'Bhagavad Gita Path';
  }

  return null;
};

const buildDerivedCardTitle = ({ benefit, deity, dosha, isRemedialCategory }) => {
  if (isRemedialCategory) {
    const mappedHighDemandPooja = getHighDemandProblemSolvingTitle({ benefit, deity, dosha });
    if (mappedHighDemandPooja && HIGHEST_DEMAND_PROBLEM_SOLVING_POOJAS.includes(mappedHighDemandPooja)) {
      return mappedHighDemandPooja;
    }
  }
  if (benefit) return `${benefit} Pooja`;
  if (dosha) return `${dosha} Nivaran Pooja`;
  if (deity) return `${deity} Blessing Pooja`;
  return 'Personalized Pooja';
};

const isRemedialProblemSolvingMatch = ({ deity, dosha, benefit }) => {
  const hasDeity = Boolean(deity);
  const hasDosha = Boolean(dosha) && normalizeValue(dosha) !== 'general';
  const hasBenefit = Boolean(benefit);
  return hasDeity && (hasDosha || hasBenefit);
};

const buildDerivedCardDescription = ({ benefit, deity, dosha, location }) => {
  const segments = [];
  if (deity) segments.push(`Dedicated to ${deity}`);
  if (benefit) segments.push(`focused on ${benefit.toLowerCase()}`);
  if (dosha && normalizeValue(dosha) !== 'general') segments.push(`for ${dosha.toLowerCase()} support`);
  if (location) segments.push(`available at ${location}`);

  if (segments.length === 0) {
    return 'Personalized spiritual ceremony crafted as per your selected intent.';
  }

  return `${segments.join(', ')}.`;
};

const OnlinePooja = () => {
  const { t } = useLanguage();
  const [selectedPooja, setSelectedPooja] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(createEmptySelections());
  const [poojaServices, setPoojaServices] = useState(poojaTypesData);
  const bookingFormRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    document.body.classList.add('online-pooja-theme');
    return () => {
      document.body.classList.remove('online-pooja-theme');
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchPoojaServices = async () => {
      try {
        const response = await apiService.getPublicPoojaServices();
        const services = Array.isArray(response.data) ? response.data : [];
        if (isMounted && response.success && services.length > 0) {
          setPoojaServices(services);
        }
      } catch (error) {
        console.warn('Using fallback pooja configuration:', error.message);
      }
    };

    fetchPoojaServices();

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePoojaSelect = (pooja) => {
    setSelectedPooja(pooja);
    bookingFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filteredPoojaTypes = useMemo(() => {
    const hasAnyFilter = Object.values(appliedFilters).some((values) => values.length > 0);
    if (!hasAnyFilter) return poojaServices;

    const matchedBasePoojas = poojaServices.filter((pooja) => {
      const poojaFilters = pooja.filters || {};

      return Object.entries(appliedFilters).every(([sectionKey, selectedValues]) => {
        if (!selectedValues || selectedValues.length === 0) return true;

        const poojaValues = Array.isArray(poojaFilters[sectionKey]) ? poojaFilters[sectionKey] : [];
        const normalizedPoojaValues = poojaValues.map((value) => normalizeValue(value));

        return selectedValues.some((selectedValue) =>
          normalizedPoojaValues.includes(normalizeValue(selectedValue))
        );
      });
    });

    const shouldRestructureCards =
      appliedFilters.deity.length > 0 ||
      appliedFilters.dosha.length > 0 ||
      appliedFilters.benefits.length > 0;

    if (!shouldRestructureCards) {
      return matchedBasePoojas;
    }

    const displayCards = [];
    const seenDerivedTitles = new Set();

    matchedBasePoojas.forEach((pooja) => {
      const isAdminConfiguredPooja = !DEFAULT_CATEGORY_POOJA_IDS.has(pooja.id);
      if (isAdminConfiguredPooja) {
        displayCards.push({
          ...pooja,
          categoryLabel: pooja.categoryLabel || 'Temple Catalogue Pooja'
        });
        return;
      }

      const poojaFilters = pooja.filters || {};
      const matchedBenefit = getCommonValue(appliedFilters.benefits, poojaFilters.benefits);
      const matchedDeity = getCommonValue(appliedFilters.deity, poojaFilters.deity);
      const matchedDosha = getCommonValue(appliedFilters.dosha, poojaFilters.dosha);
      const matchedLocation = getCommonValue(appliedFilters.location, poojaFilters.location);
      const isRemedialCategory = isRemedialProblemSolvingMatch({
        deity: matchedDeity,
        dosha: matchedDosha,
        benefit: matchedBenefit
      });

      const cardTitle = buildDerivedCardTitle({
        benefit: matchedBenefit,
        deity: matchedDeity,
        dosha: matchedDosha,
        isRemedialCategory
      });

      const cardDescription = buildDerivedCardDescription({
        benefit: matchedBenefit,
        deity: matchedDeity,
        dosha: matchedDosha,
        location: matchedLocation
      });

      const titleKey = normalizeValue(cardTitle);
      if (seenDerivedTitles.has(titleKey)) return;
      seenDerivedTitles.add(titleKey);

      displayCards.push({
        ...pooja,
        id: `${pooja.id}-${normalizeValue(cardTitle).replace(/[^a-z0-9]+/g, '-')}`,
        bookingTypeId: isRemedialCategory ? 'remedial' : pooja.id,
        categoryLabel: isRemedialCategory
          ? 'Remedial / Problem-Solving Pooja'
          : 'General Pooja',
        title: cardTitle,
        description: cardDescription,
        featured: false
      });
    });

    return displayCards;
  }, [appliedFilters, poojaServices]);

  const imageUrl = '/images/bg.avif';
  
  return (
    <div className="online-pooja-page">
      {/* This is the part we will restyle */}
      <PageBackground imageUrl={imageUrl} />

      <div className="pooja-page-header">
        <div className="container-header">
          <h1>{t('onlinePooja.pageHeader.title')}</h1>
          <p>{t('onlinePooja.pageHeader.subtitle')}</p>
        </div>
      </div>

      <div className="pooja-main-content">
        <PoojaFilters
          poojaItems={poojaServices}
          appliedSelections={appliedFilters}
          onApply={setAppliedFilters}
        />

        <PoojaTypes
          onPoojaSelect={handlePoojaSelect}
          poojaList={filteredPoojaTypes}
        />

        <div ref={bookingFormRef}>
          <BookingForm selectedPooja={selectedPooja} poojaList={poojaServices} />
        </div>
        <HowItWorks />

        <Testimonials />
      </div>
    </div>
  );
};

export default OnlinePooja;
